import React, { useState, useEffect } from 'react';
import { 
   Eye, BookOpen, Search, 
 RefreshCw,  AlertCircle, FileText,
  UserPlus, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../components/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { exportInscriptions, fetchAllInscriptionsForExport } from '../utils/exportUtils';

// Custom hook pour gérer les données d'inscription
const useInscriptionData = (currentPage, pageSize, selectedAnnee, selectedClasse, selectedNiveau, selectedFiliere, searchTerm) => {
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);
  const [erreur, setErreur] = useState(null);
  const [classes, setClasses] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);

  // Fonction pour récupérer les données
  const fetchData = async () => {
    setLoading(true);
    setErreur(null);
    try {
      // Récupérer la liste des inscriptions avec pagination et filtres
      const response = await api.get('/inscription/liste-inscriptions/', {
        params: {
          page: currentPage,
          page_size: pageSize,
          annee: selectedAnnee || '',
          classe: selectedClasse || '',
          niveau: selectedNiveau || '',
          filiere: selectedFiliere || '',
          search: searchTerm || ''
        }
      });

      // Traiter la réponse même si elle est vide
      if (response.data) {
        if (response.data.status === 'success') {
          setInscriptions(response.data.data || []);
          setTotalPages(response.data.pagination?.total_pages || 0);
          setTotalItems(response.data.pagination?.total || 0);
        } else {
          // Si pas de succès mais pas d'erreur 404, c'est probablement vide
          setInscriptions([]);
          setTotalPages(0);
          setTotalItems(0);
        }
      } else {
        setInscriptions([]);
        setTotalPages(0);
        setTotalItems(0);
      }

      // Récupérer les autres données
      const [
        filieresResponse, 
        niveauxResponse, 
        classesResponse,
        anneesResponse
      ] = await Promise.all([
        api.get('/filiere/liste-filieres/'),
        api.get('/niveau/liste-niveaux/'),
        api.get('/classe/classe-list-create/'),
        api.get('/etablissement/annees-academiques/')
      ]);

      setFilieres(filieresResponse.data.results || filieresResponse.data || []);
      setNiveaux(niveauxResponse.data.results || niveauxResponse.data || []);
      setClasses(classesResponse.data.results || classesResponse.data || []);
      setAnneesAcademiques(anneesResponse.data || []);
      
    } catch (error) {
      console.error('Erreur de chargement des données:', error);
      
      // Ne pas traiter les 404 comme des erreurs critiques
      if (error.response && error.response.status === 404) {
        setInscriptions([]);
        setTotalPages(0);
        setTotalItems(0);
        setErreur(null); // Pas d'erreur si c'est juste vide
      } else {
        setErreur(error.message || 'Erreur de chargement des données');
        toast.error('Erreur de chargement des données');
      }
    } finally {
      setLoading(false);
    }
  };

  // Charger les données au changement de page, taille, année ou filtres
  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, selectedAnnee, selectedClasse, selectedNiveau, selectedFiliere, searchTerm]);

  return {
    inscriptions,
    loading,
    totalPages,
    totalItems,
    anneesAcademiques,
    erreur,
    fetchData,
    classes,
    niveaux,
    filieres
  };
};

const InscriptionPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [selectedNiveau, setSelectedNiveau] = useState('');
  const [selectedFiliere, setSelectedFiliere] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  
  // Utiliser le custom hook avec tous les filtres
  const {
    inscriptions,
    loading,
    totalPages,
    totalItems,
    anneesAcademiques,
    erreur,
    fetchData,
    classes,
    niveaux,
    filieres
  } = useInscriptionData(
    currentPage, 
    pageSize, 
    selectedAnnee,
    selectedClasse,
    selectedNiveau,
    selectedFiliere,
    searchTerm
  );

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleAnneeChange = (e) => {
    setSelectedAnnee(e.target.value);
    setCurrentPage(1);
  };

  const handleOpenDeleteModal = (inscription) => {
    setSelectedInscription(inscription);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      if (!selectedInscription) return;
      
      const response = await api.delete(`/inscription/details-inscription/${selectedInscription.id}/`);
      
      if (response.data.status === 'success') {
        toast.success('Inscription annulée avec succès');
        fetchData(); // Rafraîchir les données
      } else {
        throw new Error(response.data.message || 'Erreur lors de l\'annulation de l\'inscription');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.message || 'Erreur lors de l\'annulation de l\'inscription');
    } finally {
      setDeleteModalOpen(false);
      setSelectedInscription(null);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClasseChange = (e) => {
    setSelectedClasse(e.target.value);
    setCurrentPage(1);
  };

  const handleNiveauChange = (e) => {
    setSelectedNiveau(e.target.value);
    setCurrentPage(1);
  };

  const handleFiliereChange = (e) => {
    setSelectedFiliere(e.target.value);
    setCurrentPage(1);
  };

  const handleViewDetails = (inscription) => {
    navigate(`/details-inscription/${inscription.id}`);
  };

  const formatMontant = (montant) => {
    return montant ? montant.toLocaleString('fr-FR') + ' FCFA' : '0 FCFA';
  };

  // Fonction pour gérer les exports
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      console.log('🚀 Début export inscriptions:', format);
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = `inscriptions_${timestamp}`;
      
      // Préparer les filtres actuels pour récupérer toutes les données
      const filters = {
        annee: selectedAnnee || '',
        classe: selectedClasse || '',
        niveau: selectedNiveau || '',
        filiere: selectedFiliere || '',
        search: searchTerm || ''
      };
      
      console.log('🔍 Filtres inscriptions:', filters);
      
      // Récupérer toutes les données avec les filtres
      console.log('📡 Récupération des inscriptions...');
      const allInscriptions = await fetchAllInscriptionsForExport(filters);
      console.log('📊 Inscriptions récupérées:', allInscriptions?.length, 'inscriptions');
      
      if (!allInscriptions || allInscriptions.length === 0) {
        toast.warning('Aucune inscription à exporter');
        return;
      }
      
      // Exporter avec les données récupérées - utiliser la bonne signature
      console.log('💾 Export inscriptions en cours...', format);
      const result = await exportInscriptions(allInscriptions, format, filename, filters);
      
      console.log('✅ Résultat export inscriptions:', result);
      
      if (result.success) {
        toast.success(result.message || `Export ${format.toUpperCase()} terminé avec succès`);
      } else {
        toast.error(result.message || 'Erreur lors de l\'export');
      }
    } catch (error) {
      console.error('❌ Erreur lors de l\'export inscriptions:', error);
      toast.error('Erreur lors de l\'export des données: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const Pagination = () => (
    <div className="flex items-center justify-between mt-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
            currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Précédent
        </button>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
            currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Suivant
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Affichage de{' '}
            <span className="font-medium">
              {((currentPage - 1) * pageSize) + 1}
            </span>{' '}
            à{' '}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{' '}
            sur{' '}
            <span className="font-medium">{totalItems}</span> inscriptions
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Précédent</span>
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === i + 1
                    ? 'z-10 bg-[#202654] border-[#202654] text-white'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Suivant</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
          <div>
                    <h1 className="text-2xl font-bold">Gestion des Inscriptions</h1>
                    <p className="mt-1 text-blue-100">
              Gérez et visualisez toutes les inscriptions des étudiants
            </p>
                  </div>
          </div>
          <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
            <button
              onClick={() => navigate('/create-inscription')}
                    className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Nouvelle Inscription
            </button>
            <button
              onClick={() => fetchData()}
                    className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </button>
                </div>
          </div>
        </div>
        
            {/* Filtres */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="relative w-64">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Rechercher un étudiant..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                />
              </div>
              
              <div className="flex flex-wrap gap-4">
                <select
                  value={selectedClasse}
                  onChange={handleClasseChange}
                  className="block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                >
                  <option value="">Toutes les classes</option>
                  {classes.map(classe => (
                    <option key={classe.id} value={classe.id}>
                      {classe.libelle}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedNiveau}
                  onChange={handleNiveauChange}
                  className="block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                >
                  <option value="">Tous les niveaux</option>
                  {niveaux.map(niveau => (
                    <option key={niveau.id} value={niveau.id}>
                      {niveau.libelle}
                    </option>
                  ))}
                </select>

                  <select
                  value={selectedFiliere}
                  onChange={handleFiliereChange}
                  className="block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#202654] focus:border-[#202654] sm:text-sm"
                >
                  <option value="">Toutes les filières</option>
                  {filieres.map(filiere => (
                    <option key={filiere.id} value={filiere.id}>
                      {filiere.libelle}
                      </option>
                    ))}
                  </select>

                <div className="flex gap-2">
                  <button
                      onClick={() => handleExport('excel')}
                      disabled={isExporting || inscriptions.length === 0}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                      {isExporting ? 'Export...' : 'Excel'}
                  </button>
                  <button
                      onClick={() => handleExport('csv')}
                      disabled={isExporting || inscriptions.length === 0}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                      {isExporting ? 'Export...' : 'CSV'}
                  </button>
                  <button
                      onClick={() => handleExport('pdf')}
                      disabled={isExporting || inscriptions.length === 0}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                      {isExporting ? 'Export...' : 'PDF'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
            </div>
          ) : erreur ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <AlertCircle className="h-10 w-10 text-red-500 mb-2" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Erreur de chargement</h3>
              <p className="text-gray-500 max-w-md">{erreur}</p>
              <button
                onClick={fetchData}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </button>
            </div>
          ) : inscriptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <FileText className="h-10 w-10 text-gray-400 mb-2" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune inscription trouvée</h3>
              <p className="text-gray-500 max-w-md">
                {selectedAnnee 
                  ? "Aucune inscription pour l'année académique sélectionnée." 
                  : "Aucune inscription n'a été trouvée dans le système."}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiant</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filière / Niveau</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Année Académique</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paiement</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {inscriptions.map((inscription) => (
                      <tr key={inscription.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#202654]/10 flex items-center justify-center">
                              <BookOpen className="h-4 w-4 text-[#202654]" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{inscription.eleve_nom} {inscription.eleve_prenom}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{inscription.classe_nom}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{inscription.filiere_nom}</div>
                          <div className="text-xs text-gray-500">{inscription.niveau_nom}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{inscription.annee_libelle}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatMontant(inscription.montant_paye)}
                          </div>
                          <div className="text-xs text-gray-500">
                            Reste: {formatMontant(inscription.montant_impaye)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            inscription.is_annuler 
                              ? 'bg-red-100 text-red-800' 
                              : inscription.status 
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {inscription.is_annuler 
                              ? 'Annulée' 
                              : inscription.status 
                                ? 'Actif'
                                : 'En attente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewDetails(inscription)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              <Eye size={18} />
                            </button>
                            {!inscription.is_annuler && (
                              <button
                                onClick={() => handleOpenDeleteModal(inscription)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <AlertCircle size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-4 border-t border-gray-200">
                <Pagination />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
              <h3 className="mt-2 text-lg font-medium text-gray-900">Annuler l'inscription</h3>
              <p className="mt-2 text-sm text-gray-500">
                Êtes-vous sûr de vouloir annuler l'inscription de {selectedInscription?.eleve_nom} {selectedInscription?.eleve_prenom} ?
                Cette action est irréversible.
              </p>
            </div>
            <div className="mt-5 flex justify-center space-x-3">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default InscriptionPage;
